import {
  DetailCard,
  EditableValue,
  Tags,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { getIcdDescription } from "../../../api/icdDescription/getIcdDescription";
import "../authorisationDetails.scss";
import {
  DIAGNOSIS_DETAIL_TITLE,
  FEEDBACK_MODE_BADGE_LABEL,
  INVALID_BADGE_LABEL,
  PLACEHOLDER,
  VALUE_CHANGED_BG,
  VALUE_CHANGED_LABEL,
  VALUE_CHANGED_TEXT,
  type ApprovalStatus,
  type DiagnosisDetailItemState,
} from "../utils/constants";
import SectionApprovalActions from "./SectionApprovalActions";

function normalizeIcdCode(raw: string | null | undefined): string {
  const t = (raw ?? "").trim();
  return t || PLACEHOLDER;
}

function singleRow(code: string, info: string): DiagnosisDetailItemState[] {
  return [{ diagnosisCode: code, diagnosisInformation: info }];
}

interface DiagnosisDetailProps {
  authLetterIcdCode?: string | null;
  agentDiffChangedKeys?: Set<keyof DiagnosisDetailItemState>;
  approvalStatus: ApprovalStatus;
  onApprovalStatusChange: (status: ApprovalStatus) => void;
  onFieldStateUpdate?: (state: DiagnosisDetailItemState[] | null) => void;
  onHasAnyChangeUpdate?: (hasAnyChange: boolean) => void;
  resetTrigger?: number;
  /**
   * When true, reject was loaded from persisted `diagnosis_validated` — no feedback-mode UI
   * until the user changes approval.
   */
  serverRejectHydrated?: boolean;
}

const DiagnosisDetail = ({
  authLetterIcdCode,
  agentDiffChangedKeys,
  approvalStatus,
  onApprovalStatusChange,
  onFieldStateUpdate,
  onHasAnyChangeUpdate,
  resetTrigger = 0,
  serverRejectHydrated = false,
}: DiagnosisDetailProps) => {
  const [originalState, setOriginalState] = useState<
    DiagnosisDetailItemState[]
  >(() => singleRow(PLACEHOLDER, PLACEHOLDER));
  const [fieldState, setFieldState] = useState<DiagnosisDetailItemState[]>(() =>
    singleRow(PLACEHOLDER, PLACEHOLDER),
  );
  const [descriptionLoading, setDescriptionLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const code = normalizeIcdCode(authLetterIcdCode);
    const load = async () => {
      setDescriptionLoading(true);
      const info =
        code === PLACEHOLDER ? PLACEHOLDER : await getIcdDescription(code);
      if (cancelled) return;
      const row = singleRow(code, info);
      setOriginalState(row);
      setFieldState(row);
      setDescriptionLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [authLetterIcdCode, resetTrigger]);

  const handleCodeSave = useCallback(async (v: string) => {
    const code = normalizeIcdCode(v);
    setDescriptionLoading(true);
    setFieldState((prev) => {
      const next = [...prev];
      next[0] = {
        ...next[0],
        diagnosisCode: code,
        diagnosisInformation: PLACEHOLDER,
      };
      return next;
    });
    const info =
      code === PLACEHOLDER ? PLACEHOLDER : await getIcdDescription(code);
    setFieldState((prev) => {
      const next = [...prev];
      next[0] = { ...next[0], diagnosisInformation: info };
      return next;
    });
    setDescriptionLoading(false);
  }, []);

  const changedCount = fieldState.filter(
    (item, i) =>
      item.diagnosisCode !== originalState[i]?.diagnosisCode ||
      item.diagnosisInformation !== originalState[i]?.diagnosisInformation,
  ).length;
  const hasAnyChange = changedCount > 0;

  useEffect(() => {
    onFieldStateUpdate?.(fieldState);
    return () => {
      onFieldStateUpdate?.(null);
    };
  }, [fieldState, onFieldStateUpdate]);

  useEffect(() => {
    onHasAnyChangeUpdate?.(hasAnyChange);
  }, [hasAnyChange, onHasAnyChangeUpdate]);

  return (
    <DetailCard
      actions={
        <SectionApprovalActions
          approvalStatus={approvalStatus}
          onApprovalStatusChange={onApprovalStatusChange}
          variant="chips"
        />
      }
      badge={
        approvalStatus ? (
          hasAnyChange ? (
            <Tags
              label={VALUE_CHANGED_LABEL}
              backgroundColor={VALUE_CHANGED_BG}
              textColor={VALUE_CHANGED_TEXT}
            />
          ) : (
            <SectionApprovalActions
              approvalStatus={approvalStatus}
              onApprovalStatusChange={onApprovalStatusChange}
              variant="badge"
              invalidBadgeLabel={
                approvalStatus === "invalid" && serverRejectHydrated
                  ? INVALID_BADGE_LABEL
                  : FEEDBACK_MODE_BADGE_LABEL
              }
            />
          )
        ) : undefined
      }
      title={DIAGNOSIS_DETAIL_TITLE}
      headerClassName="font-semibold leading-[1.375rem] text-[0.875rem] text-primaryGray-1 [font-family:Lato]"
    >
      <div className="inline-flex items-start justify-end">
        {fieldState.map((item, index) => {
          const original = originalState[index];
          const isCodeChanged = Boolean(
            (original && item.diagnosisCode !== original.diagnosisCode) ||
            agentDiffChangedKeys?.has("diagnosisCode"),
          );
          return (
            <div
              key={index}
              className="authorisation-details__row authorisation-details__row"
            >
              <div className="authorisation-details__field">
                <div className="authorisation-details__label">
                  Diagnosis Code
                  {isCodeChanged && (
                    <Tags
                      label={VALUE_CHANGED_LABEL}
                      backgroundColor={VALUE_CHANGED_BG}
                      textColor={VALUE_CHANGED_TEXT}
                    />
                  )}
                </div>
                <EditableValue
                  value={item.diagnosisCode}
                  onSave={(v) => {
                    void handleCodeSave(v);
                  }}
                  placeholder="Diagnosis Code"
                  showEditButton={
                    approvalStatus === "invalid" && !serverRejectHydrated
                  }
                  className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]"
                />
              </div>
              <div className="authorisation-details__field">
                <div className="authorisation-details__label">
                  Diagnosis Information
                </div>
                <div
                  className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]"
                  aria-busy={descriptionLoading}
                >
                  {descriptionLoading ? "Loading…" : item.diagnosisInformation}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DetailCard>
  );
};

export default DiagnosisDetail;
